import React from "react";
import { Box, Stack, Typography, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { StatusDotIcon } from "../../../../components/mui/index.js";

const positiveInvoiceActions = [
  "update_sms_overage",
  "update_customer_overage",
];

export function generateLogsTooltip(changelogs) {
  return (
    <Box
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        p: 1,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#bbb",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
      }}
    >
      <Stack>
        {changelogs.map((log) => (
          <Stack
            key={log.id}
            spacing={1}
            sx={{ mb: 1, borderBottom: "1px solid #ccc" }}
          >
            <Typography variant="body2">
              {log.result_status === "error" ? "❌" : "✅"}{" "}
              {dayjs(log.created_when).format("MMM D, YYYY h:mma")}:{" "}
            </Typography>
            <Typography variant="body2">{log.action}</Typography>
            <Typography variant="body2">
              {log.changed_fields ? JSON.stringify(log.changed_fields) : ""}
            </Typography>
            <Typography variant="body2">{log.result_message}</Typography>
            {log.result_message?.includes("Craig Carlson") && (
              <Typography variant="body2">
                This means the invoice is likely open in another web browser and
                cannot be saved via the api while it is open
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export function buildTooltipSummary(data) {
  const sentLog = data.changelogs?.find(
    (log) =>
      log.result_status === "success" &&
      log.action.includes("send_customer_invoice"),
  );

  return (
    <Box
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        p: 1,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#bbb",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
      }}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Invoice Summary
        </Typography>
        <Stack>
          <Typography sx={{ textDecoration: "underline" }}>
            Line Item Updates:
          </Typography>
          {data.changelogs.filter(
            (log) =>
              log.result_status === "success" &&
              positiveInvoiceActions.includes(log.action),
          ).length > 0 ? (
            <Stack>
              {!!data.changelogs.find(
                (log) =>
                  log.result_status === "success" &&
                  log.action === "update_sms_overage",
              ) ? (
                <Typography variant="body2">
                  ✅ - SMS Overage Updated{" "}
                </Typography>
              ) : (
                <Typography variant="body2">
                  ❌ - No SMS overage updates found{" "}
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2">No invoice updates found</Typography>
          )}
        </Stack>
        <Stack>
          <Typography sx={{ textDecoration: "underline" }}>
            Billed Status:
          </Typography>
          {data.changelogs?.some(
            (log) =>
              log.action === "charge_stripe_customer" &&
              log.result_status === "success",
          ) ? (
            <Typography variant="body2">
              ✅ - Billed to Customer on{" "}
              {dayjs(
                data.changelogs.find(
                  (log) =>
                    log.action === "charge_stripe_customer" &&
                    log.result_status === "success",
                ).created_when,
              ).format("MMM D, YYYY h:mma")}
            </Typography>
          ) : (
            <Typography variant="body2">
              ❌ - Not yet billed to customer
            </Typography>
          )}
        </Stack>
        <Stack>
          <Typography sx={{ textDecoration: "underline" }}>
            Payment Status:
          </Typography>
          {data.invoice.Balance === 0 ? (
            <Stack>
              <Typography variant="body2">
                ✅ - Invoice marked as Fully Paid in QuickBooks
              </Typography>
              {data.changelogs?.some(
                (log) =>
                  log.action === "create_quickbooks_payment" &&
                  log.result_status === "success",
              ) ? (
                <Typography variant="body2">
                  ✅ - QuickBooks Payment created on{" "}
                  {dayjs(
                    data.changelogs.find(
                      (log) =>
                        log.action === "create_quickbooks_payment" &&
                        log.result_status === "success",
                    ).created_when,
                  ).format("MMM D, YYYY h:mma")}
                </Typography>
              ) : (
                <Typography variant="body2">
                  ❌ - No QuickBooks Payment creation found
                </Typography>
              )}
            </Stack>
          ) : (
            <Stack>
              <Typography variant="body2">
                ❌ - Invoice not marked as Paid in QuickBooks
              </Typography>
              <Typography variant="body2">
                Current Balance: $
                {typeof data.invoice.Balance === "number"
                  ? data.invoice.Balance.toFixed(2)
                  : "--"}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack>
          <Typography sx={{ textDecoration: "underline" }}>
            Sent Status:
          </Typography>
          {data.invoice.EmailStatus === "EmailSent" ||
          data.invoice.EInvoiceStatus === "Sent" ||
          !!sentLog ? (
            <Stack></Stack>
          ) : (
            <Stack>
              <Typography variant="body2">No sending actions found</Typography>
            </Stack>
          )}
          {!!sentLog && (
            <Typography variant="body2">
              ✅ - Sent to Customer on{" "}
              {dayjs(sentLog.created_when).format("MMM D, YYYY h:mma")}
            </Typography>
          )}
          <Typography variant="body2">
            {data.invoice.EmailStatus === "EmailSent" ? "✅ - " : "❌ -"} Email
            Status: {data.invoice.EmailStatus}
          </Typography>
          <Typography variant="body2">
            {data.invoice.EInvoiceStatus === "Sent" ? "✅ - " : "❌ -"} Invoice
            Opened by Customer:{" "}
            {data.invoice.EInvoiceStatus
              ? data.invoice.EInvoiceStatus
              : "NotSet"}{" "}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

export function generateTotalAmountTooltip(data) {
  return (
    <Box
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        p: 1,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#bbb",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
        Line Items
      </Typography>
      <Stack>
        {data.invoice?.Line?.filter(
          (line) => line.DetailType !== "SubTotalLineDetail",
        )
          .sort((a, b) => (a.LineNum ?? 0) - (b.LineNum ?? 0))
          .map((lineItem, index) => (
            <Stack
              key={lineItem.Id || index}
              spacing={1}
              sx={{ mb: 1, borderBottom: "1px solid #ccc" }}
            >
              <Typography variant="body2">
                <strong>Line Number:</strong> {lineItem.LineNum ?? "--"}
              </Typography>
              <Typography variant="body2">
                <strong>Description:</strong> {lineItem.Description ?? "--"}
              </Typography>
              <Typography variant="body2">
                <strong>Detail Type:</strong> {lineItem.DetailType ?? "--"}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong>{" "}
                {typeof lineItem.Amount === "number"
                  ? `$${lineItem.Amount.toFixed(2)}`
                  : "--"}
              </Typography>
              <Typography variant="body2">
                <strong>Line Id:</strong> {lineItem.Id ?? "--"}
              </Typography>
              {lineItem.SalesItemLineDetail && (
                <>
                  <Typography variant="body2">
                    <strong>Item:</strong>{" "}
                    {lineItem.SalesItemLineDetail.ItemRef?.name ?? "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Item Value:</strong>{" "}
                    {lineItem.SalesItemLineDetail.ItemRef?.value ?? "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Qty:</strong>{" "}
                    {lineItem.SalesItemLineDetail.Qty ?? "--"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Unit Price:</strong>{" "}
                    {typeof lineItem.SalesItemLineDetail.UnitPrice === "number"
                      ? `$${lineItem.SalesItemLineDetail.UnitPrice.toFixed(2)}`
                      : "--"}
                  </Typography>
                </>
              )}
            </Stack>
          ))}

        <Typography variant="body2">
          <strong>Subtotal:</strong>{" "}
          {data.invoice?.Line?.find(
            (line) => line.DetailType === "SubTotalLineDetail",
          )?.Amount
            ? `$${data.invoice.Line.find(
                (line) => line.DetailType === "SubTotalLineDetail",
              ).Amount.toFixed(2)}`
            : "--"}
        </Typography>

        <Typography variant="body2">
          <strong>Total Amount:</strong>{" "}
          {data.invoice?.TotalAmt
            ? `$${data.invoice.TotalAmt.toFixed(2)}`
            : "--"}
        </Typography>
      </Stack>
    </Box>
  );
}

export function generateSmsOverageTooltip(data, metaData) {
  return (
    <Box
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        p: 1,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#bbb",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
      }}
    >
      <Stack spacing={1}>
        <Typography variant="body2">
          <strong>
            {" "}
            {metaData?.dates?.overage_month
              ? `${dayjs(metaData.dates.overage_month).format(
                  "MMMM YYYY",
                )} Overages:`
              : "This Month Overage:"}{" "}
          </strong>
          {data?.overages_and_usage?.usage_sms_credits?.current
            ?.overage_usage ?? "--"}
        </Typography>

        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_month
              ? `${dayjs(metaData.dates.overage_month).format(
                  "MMMM YYYY",
                )} Overage Unit Cost:`
              : "This Month Overage Unit Cost:"}{" "}
          </strong>
          {data?.overages_and_usage?.usage_sms_credits?.current
            ?.overage_per_unit
            ? `$${data.overages_and_usage.usage_sms_credits.current.overage_per_unit.toFixed(
                2,
              )}`
            : "--"}
        </Typography>
        <Typography variant="body2">-------</Typography>
        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_last_month
              ? `${dayjs(metaData.dates.overage_last_month).format(
                  "MMMM YYYY",
                )} Overages:`
              : "Last Month Overage:"}{" "}
          </strong>
          {data?.overages_and_usage?.usage_sms_credits?.last_month
            ?.overage_usage ?? "--"}
        </Typography>
        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_last_month
              ? `${dayjs(metaData.dates.overage_last_month).format(
                  "MMMM YYYY",
                )} Overage Unit Cost:`
              : "Last Month Overage Unit Cost:"}{" "}
          </strong>
          {data?.overages_and_usage?.usage_sms_credits?.last_month
            ?.overage_per_unit
            ? `$${data.overages_and_usage.usage_sms_credits.last_month.overage_per_unit.toFixed(
                2,
              )}`
            : "--"}
        </Typography>
      </Stack>
    </Box>
  );
}

export function generateContactOverageTooltip(data, metaData) {
  return (
    <Box
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        p: 1,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#bbb",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
      }}
    >
      <Stack spacing={1}>
        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_month
              ? `${dayjs(metaData.dates.overage_month).format(
                  "MMMM YYYY",
                )}  Unbilled Blocks:`
              : "This Month Contact Overage - Unbilled Blocks:"}{" "}
          </strong>
          {data?.overages_and_usage?.additional_usage_base_contacts?.current
            ?.overage_usage ?? "--"}
        </Typography>

        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_month
              ? `${dayjs(metaData.dates.overage_month).format(
                  "MMMM YYYY",
                )} Ten K more:`
              : "This Month Overage Ten K More:"}{" "}
          </strong>
          {data?.overages_and_usage?.additional_usage_base_contacts?.current
            ?.overage_per_unit ?? "--"}
        </Typography>

        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_month
              ? `${dayjs(metaData.dates.overage_month).format(
                  "MMMM YYYY",
                )} Unbilled Dollars:`
              : "This Month Overage Unbilled Dollars:"}{" "}
          </strong>
          {Number(data?.overages_and_usage?.additional_usage_base_contacts?.current?.overage_usage) &&
          Number(data?.overages_and_usage?.additional_usage_base_contacts?.current?.overage_per_unit)
            ? `$${(
                Number(data.overages_and_usage.additional_usage_base_contacts.current.overage_usage) *
                Number(data.overages_and_usage.additional_usage_base_contacts.current.overage_per_unit)
              ).toFixed(2)}`
            : "--"}
        </Typography>

        {/*         <Typography variant="body2">-------</Typography>
        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_last_month
              ? `${dayjs(metaData.dates.overage_last_month).format(
                  "MMMM YYYY",
                )} Contact Overages:`
              : "Last Month Contact Overage:"}{" "}
          </strong>
          {data?.overages_and_usage?.additional_usage_base_contacts?.last_month
            ?.overage_usage ?? "--"}
        </Typography>
        <Typography variant="body2">
          <strong>
            {metaData?.dates?.overage_last_month
              ? `${dayjs(metaData.dates.overage_last_month).format(
                  "MMMM YYYY",
                )} Overage Unit Cost:`
              : "Last Month Overage Unit Cost:"}{" "}
          </strong>
          {data?.overages_and_usage?.additional_usage_base_contacts?.last_month
            ?.overage_per_unit
            ? `$${data.overages_and_usage.additional_usage_base_contacts.last_month.overage_per_unit.toFixed(
                2,
              )}`
            : "--"}
        </Typography> */}
      </Stack>
    </Box>
  );
}

export function determineInvoiceStatus(data) {
  const hasSentLog = data.changelogs?.some(
    (log) =>
      log.result_status === "success" &&
      log.action.includes("send_customer_invoice"),
  );

  if (
    data.invoice.EmailStatus === "EmailSent" ||
    data.invoice.EInvoiceStatus === "Sent" ||
    hasSentLog
  ) {
    return (
      <Tooltip title={<Typography>{buildTooltipSummary(data)}</Typography>}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: "bold" }}>Sent to Customer</Typography>
          <StatusDotIcon
            fill={
              data.invoice.EmailStatus === "EmailSent" && hasSentLog
                ? "#5d8ef8ff"
                : "#ffcc00"
            }
          />
        </Stack>
      </Tooltip>
    );
  }

  const hasBilledInvoiceLog = data.changelogs?.some(
    (log) =>
      log.action === "charge_stripe_customer" &&
      log.result_status === "success",
  );
  if (hasBilledInvoiceLog) {
    return (
      <Tooltip title={<Typography>{buildTooltipSummary(data)}</Typography>}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ textDecoration: "underline" }}>
            Billed to Customer
          </Typography>
          <StatusDotIcon fill={"#3bb143"} />
        </Stack>
      </Tooltip>
    );
  }

  const hasUpdatedInvoiceLog = data.changelogs?.some(
    (log) =>
      positiveInvoiceActions.includes(log.action) &&
      log.result_status === "success",
  );

  if (hasUpdatedInvoiceLog) {
    return (
      <Tooltip title={<Typography>{buildTooltipSummary(data)}</Typography>}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ textDecoration: "underline" }}>
            Updated (Not Charged or Sent)
          </Typography>
          <StatusDotIcon fill={"#3bb143"} />
        </Stack>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        <Stack spacing={1}>
          <Typography>
            This either means we haven't attempted to make any changes to this
            invoice, or if some error is obvious, then not we're logging that
            error
          </Typography>
          <Typography>{buildTooltipSummary(data)}</Typography>
        </Stack>
      }
    >
      <Typography sx={{ fontStyle: "italic" }}> No actions found </Typography>
    </Tooltip>
  );
}
